/* eslint-disable complexity */
import React, { useCallback, useState, useEffect }  from 'react';
import PropTypes from 'prop-types';

import Tag from '@ux/tag';
import ChevronDown from '@ux/icon/chevron-down';
import WindowNew from '@ux/icon/window-new';
import PlusIcon from '@ux/icon/plus';

import classNames from 'classnames';
import useIsMobile from '../lib/useIsMobile';
import NavigationLink from './NavigationLink';
import { useGetWindowHref } from '../lib/useGetWindowHref';
import { isNavItemActive } from '../lib/linkUtils';
import { useRootProps } from './UserDataProvider';

const IconLabelAndBadge = ({
  navItem,
  icon,
  index,
  affixed
}) => {
  const eidPart = navItem.href?.substring(
    navItem.href.indexOf('.com') + 5,
    navItem.href.lastIndexOf('/')
  );

  const tourId = `reactour__${ eidPart || '' }`;
  const { split } = useRootProps();
  const navItemNotificationDot = split?.includes('nearstar') && navItem.notificationDot?.show;

  return (
    <>
      <span className='nav-link-icon' data-tut={ affixed ? tourId : '' } style={{ '--item-index': index }}>
        { icon }
      </span>
      <span className='nav-link-label' data-tut={ affixed ? tourId : '' }>
        <span className='nav-link-text'>{ navItem.caption }</span>
        { navItemNotificationDot && (
          <span className={ classNames('nav-notification-dot') } data-testid='nav-notification-dot'>
            { navItem.notificationDot?.number }
          </span>
        )}
        { navItem.badge?.text ? (
          <Tag type='highlight' size='sm' className='ml-3 p-1'>{ navItem.badge.text }</Tag>
        ) : null }
      </span>
    </>
  );
};

IconLabelAndBadge.propTypes = {
  navItem: PropTypes.shape({
    icon: PropTypes.string,
    badge: PropTypes.shape({
      text: PropTypes.string
    }),
    notificationDot: PropTypes.shape({
      show: PropTypes.bool,
      number: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf([null])
      ])
    }),
    className: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func,
    caption: PropTypes.node,
    title: PropTypes.node,
    eid: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.object)
  }),
  icon: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string
  ]),
  index: PropTypes.number,
  affixed: PropTypes.bool
};

const NavGroupItem = ({
  index,
  onClose,
  navItem,
  icon,
  isActive = false,
  affixed,
  hovered,
  sidebarNavLinkHandler }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    setIsOpen(isActive);
  }, [isActive]);

  const toggleSubNav = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const navItemKey = navItem?.caption;

  // replace uppercase with lowercase letters, and
  // replace spaces, non-letter, or non-number characters with underscores
  const eidSpecificSection = navItemKey?.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
  const subnavToggleEid = navItem?.eid
  || `application_sidebar.primary_nav_item_has_children.${eidSpecificSection}.subnav_toggle_button_open_${isOpen}.click`;
  const subnavToggleCaretOnlyEid = navItem?.eid
  || 'application_sidebar.primary_nav_item_has_children_has_href.' + eidSpecificSection +
  `.subnav_toggle_caret_only_button_open_${isOpen}.click`;

  const isCondensedSpacing = navItem?.design?.split(' ').includes('condensed-spacing');
  const classname = classNames('subnav-link-wrapper', {
    'open': isOpen,
    'condensed-spacing': isCondensedSpacing
  }, navItem.className);
  return (
    <li
      key={ `navgroup-item-li-${navItemKey}` }
      className={ classname }>
      {navItem.href ? (<>
        <NavigationLink
          link={ navItem }
          className={ classNames('sidebar-nav-link', { active: isActive, open: isOpen }) }
          key={ `navlink-item-${navItemKey}` }
          sidebarNavLinkHandler={ sidebarNavLinkHandler }
          onClose={ onClose }
        >
          <div className='nav-link-inner'>
            <IconLabelAndBadge
              navItem={ navItem }
              icon={ icon }
              index={ index }
              affixed={ affixed }
            />
          </div>
        </NavigationLink>
        <button
          data-eid={ subnavToggleCaretOnlyEid }
          aria-label={ navItem.caption }
          type='button'
          onClick={ toggleSubNav }
          className={ classNames('sidebar-nav-link-toggle-btn', { open: isOpen }) }
        >
          <div className='nav-link-inner'>
            <ChevronDown className='nav-link-subnav-toggle' />
          </div>
        </button>
      </>) : (<button
        aria-label={ navItem.caption }
        type='button'
        onClick={ toggleSubNav }
        className={ classNames('sidebar-nav-link', {
          active: isActive,
          open: isOpen
        }) }
        data-eid={ subnavToggleEid }
      >
        <div className='nav-link-inner'>
          <IconLabelAndBadge
            navItem={ navItem }
            icon={ icon }
            index={ index }
            affixed={ affixed }
          />
          <div className='nav-toggle-wrap nav-link-subnav-toggle'>
            <ChevronDown />
          </div>
        </div>
      </button>)}
      <ul className={ classNames('nav-subnav-links', { open: isOpen && (affixed || hovered || isMobile) }) }>
        {navItem.children.map((subNavItem) => {
          return <NavigationItem
            onClose={ onClose }
            className={ 'nav-subnav-item' }
            sidebarNavLinkHandler={ sidebarNavLinkHandler }
            key={ subNavItem.caption }
            navItem={ subNavItem }
          />;
        })}
      </ul>
    </li>
  );
};

NavGroupItem.propTypes = {
  navItem: PropTypes.shape({
    icon: PropTypes.string,
    className: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func,
    caption: PropTypes.node,
    title: PropTypes.node,
    eid: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.object),
    design: PropTypes.string
  }),
  icon: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.string
  ]),
  index: PropTypes.number,
  isActive: PropTypes.bool,
  hovered: PropTypes.bool,
  affixed: PropTypes.bool,
  onClose: PropTypes.func,
  sidebarNavLinkHandler: PropTypes.func
};

const NavigationItem = ({
  navItem,
  icon,
  index,
  affixed,
  hovered,
  onClose,
  sidebarNavLinkHandler,
  primaryActiveNavLink,
  className
}) => {
  const navItemKey = navItem?.caption;
  const browserUrlStr = useGetWindowHref();
  const activeBrowserUrl = primaryActiveNavLink ? primaryActiveNavLink : browserUrlStr;
  const shouldNotActivate = navItem?.design?.split(' ').includes('no-activate');
  const isItemActive = shouldNotActivate ? false : isNavItemActive(navItem, activeBrowserUrl);

  const { split } = useRootProps();
  const navItemNotificationDot = split?.includes('nearstar') && navItem.notificationDot?.show;

  if (navItem.children && navItem.children.length > 0) {
    return (
      <NavGroupItem
        onClose={ onClose }
        key={ `navgroup-item-${navItemKey}` }
        index={ index }
        navItem={ navItem }
        icon={ icon }
        isActive={ isItemActive }
        affixed={ affixed }
        hovered={ hovered }
        sidebarNavLinkHandler={ sidebarNavLinkHandler }
      />
    );
  }

  const eidPart = navItem.href?.substring(
    navItem.href.indexOf('.com') + 5,
    navItem.href.lastIndexOf('/')
  );

  const tourId = `reactour__${ eidPart || '' }`;

  const hasExternalLink = navItem?.design?.split(' ').includes('external-link');
  const isSubHeadingWithPlusIcon = navItem?.design?.split(' ').includes('subheading-with-plus-icon');
  const isCondensedSpacing = navItem?.design?.split(' ').includes('condensed-spacing');

  if (isSubHeadingWithPlusIcon) {
    return (
      <li className={ classNames('subheading-nav-item', { 'condensed-spacing': isCondensedSpacing },
        navItem.className,
        className
      ) }>
        <span className='subheading-label'>
          { navItem.caption }
        </span>
        <button type='button' className='add-button'>
          <PlusIcon className='plus-icon'/>
        </button>
      </li>
    );
  }

  return (
    <li className={ classNames({
      'subheading-nav-link': isSubHeadingWithPlusIcon,
      'condensed-spacing': isCondensedSpacing
    }, className) }>
      <NavigationLink
        link={ navItem }
        onClose={ onClose }
        className={ classNames('sidebar-nav-link', { active: isItemActive }, navItem.className) }
        sidebarNavLinkHandler={ sidebarNavLinkHandler }
      >
        <div className='nav-link-inner'>
          <span className='nav-link-icon' data-tut={ affixed ? tourId : '' } style={{ '--item-index': index }}>
            {(!isSubHeadingWithPlusIcon && icon) ? icon : <span />}</span>

          <span className='nav-link-label' data-tut={ affixed ? tourId : '' }>
            <span className='nav-link-text'>{ navItem.caption }</span>
            { hasExternalLink && <WindowNew className='external-link' />}
            { navItemNotificationDot && (
              <span className={ classNames('nav-notification-dot') } data-testid='nav-notification-dot'>
                { navItem.notificationDot?.number }
              </span>
            )}
            { navItem.badge?.text ? (
              <Tag emphasis='highlight' className='ml-3 p-1'>{ navItem.badge.text }</Tag>
            ) : null }
          </span>
        </div>
      </NavigationLink>
    </li>);
};

NavigationItem.propTypes = {
  navItem: PropTypes.shape({
    icon: PropTypes.string,
    badge: PropTypes.shape({
      text: PropTypes.string
    }),
    className: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func,
    caption: PropTypes.node,
    title: PropTypes.node,
    design: PropTypes.string,
    eid: PropTypes.string,
    children: PropTypes.arrayOf(PropTypes.object),
    notificationDot: PropTypes.shape({
      show: PropTypes.bool,
      number: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.oneOf([null])
      ])
    })
  }),
  icon: PropTypes.node,
  index: PropTypes.number,
  isActive: PropTypes.bool,
  hovered: PropTypes.bool,
  affixed: PropTypes.bool,
  onClose: PropTypes.func,
  sidebarNavLinkHandler: PropTypes.func,
  primaryActiveNavLink: PropTypes.string,
  className: PropTypes.string
};

export default NavigationItem;
